import { Link, LinkProps } from 'react-router-dom';
import { useCallback, useState, useEffect, useRef, ReactNode } from 'react';

// Define route categories for smart prefetching
const LIGHT_ROUTES: Record<string, () => Promise<unknown>> = {
  '/': () => import('@/pages/HomePage'),
  '/news': () => import('@/pages/NewsPage'),
  '/reviews/2026': () => import('@/pages/ReviewHub2026Page'),
  '/events': () => import('@/pages/EventsPage'),
  '/featured': () => import('@/pages/FeaturedPage'),
  '/about': () => import('@/pages/AboutPage'),
  '/contact': () => import('@/pages/ContactPage'),
};

// Heavy routes - only prefetch on explicit interaction, not on mount
const HEAVY_ROUTES: Record<string, () => Promise<unknown>> = {
  '/deals/builder': () => import('@/pages/DealBuilderLandingPage'),
  '/deals/builder/create': () => import('@/pages/DealBuilderPage'),
};

// All routes combined for lookup
const ALL_ROUTES = { ...LIGHT_ROUTES, ...HEAVY_ROUTES };

// Dynamic detail routes — matched by prefix because the slug varies.
// Importing the page chunk warms the same code that any /news/<slug> visit needs.
const DYNAMIC_ROUTE_LOADERS: Array<{ prefix: string; load: () => Promise<unknown> }> = [
  { prefix: '/news/', load: () => import('@/pages/ArticleDetailPage') },
  { prefix: '/reviews/2026/', load: () => import('@/pages/VehicleHubDetailPage') },
  { prefix: '/events/', load: () => import('@/pages/EventDetailPage') },
];

function resolveLoader(to: string): (() => Promise<unknown>) | null {
  if (ALL_ROUTES[to]) return ALL_ROUTES[to];
  const dyn = DYNAMIC_ROUTE_LOADERS.find((r) => to.startsWith(r.prefix));
  return dyn ? dyn.load : null;
}

interface PrefetchLinkProps extends Omit<LinkProps, 'to'> {
  to: string;
  children: ReactNode;
  prefetchOnMount?: boolean; // Only for light critical links
  prefetchOnIdle?: boolean; // Prefetch when browser is idle
  prefetchOnVisible?: boolean; // Prefetch when link scrolls into view (cheap, network idle)
}

export function PrefetchLink({ 
  to, 
  children, 
  onMouseEnter, 
  prefetchOnMount = false,
  prefetchOnIdle = false,
  prefetchOnVisible = false,
  ...props 
}: PrefetchLinkProps) {
  const [prefetched, setPrefetched] = useState(false);
  const anchorRef = useRef<HTMLAnchorElement | null>(null);

  // Check if this is a heavy route (dashboards, deal builder)
  const isHeavyRoute = to in HEAVY_ROUTES;
  
  // Prefetch on mount only for LIGHT routes
  useEffect(() => {
    if (prefetchOnMount && !prefetched && !isHeavyRoute && LIGHT_ROUTES[to]) {
      LIGHT_ROUTES[to]();
      setPrefetched(true);
    }
  }, [prefetchOnMount, prefetched, to, isHeavyRoute]);

  // Prefetch on idle for non-heavy routes
  useEffect(() => {
    const loader = resolveLoader(to);
    if (prefetchOnIdle && !prefetched && !isHeavyRoute && loader) {
      if ('requestIdleCallback' in window) {
        const id = (window as Window & typeof globalThis & { requestIdleCallback: (cb: () => void) => number }).requestIdleCallback(() => {
          if (!prefetched) {
            loader();
            setPrefetched(true);
          }
        });
        return () => {
          if ('cancelIdleCallback' in window) {
            (window as Window & typeof globalThis & { cancelIdleCallback: (id: number) => void }).cancelIdleCallback(id);
          }
        };
      }
    }
  }, [prefetchOnIdle, prefetched, to, isHeavyRoute]);

  // Prefetch when link scrolls into the viewport. Uses IntersectionObserver and
  // defers the actual import to requestIdleCallback so we never compete with
  // critical rendering work.
  useEffect(() => {
    if (!prefetchOnVisible || prefetched || isHeavyRoute) return;
    const loader = resolveLoader(to);
    if (!loader) return;
    const el = anchorRef.current;
    if (!el || typeof IntersectionObserver === 'undefined') return;

    const observer = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        observer.disconnect();
        const run = () => {
          if (!prefetched) {
            loader();
            setPrefetched(true);
          }
        };
        if ('requestIdleCallback' in window) {
          (window as Window & typeof globalThis & { requestIdleCallback: (cb: () => void) => number }).requestIdleCallback(run);
        } else {
          setTimeout(run, 250);
        }
      }
    }, { rootMargin: '200px' });

    observer.observe(el);
    return () => observer.disconnect();
  }, [prefetchOnVisible, prefetched, to, isHeavyRoute]);

  const handleMouseEnter = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement>) => {
      const loader = resolveLoader(to);
      if (!prefetched && loader) {
        // Prefetch on hover - works for all routes
        loader();
        setPrefetched(true);
      }
      onMouseEnter?.(e);
    },
    [to, prefetched, onMouseEnter]
  );

  // Touch start for mobile - only for light routes
  const handleTouchStart = useCallback(() => {
    const loader = resolveLoader(to);
    if (!prefetched && loader && !isHeavyRoute) {
      loader();
      setPrefetched(true);
    }
  }, [to, prefetched, isHeavyRoute]);

  return (
    <Link 
      to={to} 
      ref={anchorRef}
      onMouseEnter={handleMouseEnter} 
      onTouchStart={handleTouchStart}
      {...props}
    >
      {children}
    </Link>
  );
}

/**
 * Prefetch a route programmatically when browser is idle
 * Use this sparingly - only for routes the user is very likely to visit
 */
export function prefetchRouteOnIdle(path: string): void {
  const loader = resolveLoader(path);
  if (loader && 'requestIdleCallback' in window) {
    (window as Window & typeof globalThis & { requestIdleCallback: (cb: () => void) => void }).requestIdleCallback(() => {
      loader();
    });
  }
}
